import * as _ from "lodash";
var fp = require('lodash/fp');

type DictionaryOrNull<T> = _.Dictionary<T> | null | undefined;
type NumericDictionaryOrNull<T> = _.NumericDictionary<T> | null | undefined;
type TypeOrNull<T> = T | null | undefined;
type MergedTypes2<TObject, TSource> = TObject & TSource;
type MergedTypes3<TObject, TSource1, TSource2> = TObject & TSource1 & TSource2;
type MergedTypes4<TObject, TSource1, TSource2, TSource3> = TObject & TSource1 & TSource2 & TSource3;
type MergedTypes5<TObject, TSource1, TSource2, TSource3, TSource4> = TObject & TSource1 & TSource2 & TSource3 & TSource4;

interface IMap<T> {
    [key: string]: T;
}

/**
 * Gets the property value at path of object. If the resolved value is undefined the defaultValue is used
 * in its place.
 *
 * @param object The object to query.
 * @param path The path of the property to get.
 * @param defaultValue The value returned if the resolved value is undefined.
 * @return Returns the resolved value.
 */
function get<TObject extends object, TKey extends keyof TObject>(
    object: TObject,
    path: TKey | [TKey]
): TObject[TKey];

/**
 * @see _.get
 */
function get<TObject extends object, TKey extends keyof TObject>(
    object: TObject | null | undefined,
    path: TKey | [TKey]
): TObject[TKey] | undefined;

/**
 * @see _.get
 */
function get<TObject extends object, TKey extends keyof TObject, TDefault>(
    object: TObject | null | undefined,
    path: TKey | [TKey],
    defaultValue: TDefault
): TObject[TKey] | TDefault;

/**
 * @see _.get
 */
function get<T>(
    object: _.NumericDictionary<T>,
    path: number
): T;

/**
 * @see _.get
 */
function get<T>(
    object: NumericDictionaryOrNull<T>,
    path: number
): T | undefined;

/**
 * @see _.get
 */
function get<T, TDefault>(
    object: NumericDictionaryOrNull<T>,
    path: number,
    defaultValue: TDefault
): T | TDefault;

/**
 * @see _.get
 */
function get<TDefault>(
    object: null | undefined,
    path: _.PropertyPath,
    defaultValue: TDefault
): TDefault;

/**
 * @see _.get
 */
function get(
    object: null | undefined,
    path: _.PropertyPath
): undefined;

function get(
    object: any,
    path: _.PropertyPath,
    defaultValue?: any
): any;
function get(object, path, defaultValue?) {
    if (defaultValue !== undefined) {
        return _.get(object, path, defaultValue);
    } else {
        return _.get(object, path);
    }
}

/**
 * Checks if `path` is a direct property of `object`.
 *
 * @category Object
 * @param object The object to query.
 * @param path The path to check.
 * @returns Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = { 'a': { 'b': { 'c': 3 } } };
 * var other = _.create({ 'a': _.create({ 'b': _.create({ 'c': 3 }) }) });
 *
 * _.has(object, 'a');
 * // => true
 *
 * _.has(object, 'a.b.c');
 * // => true
 *
 * _.has(object, ['a', 'b', 'c']);
 * // => true
 *
 * _.has(other, 'a');
 * // => false
 */
function has<T>(
    object: T,
    path: _.PropertyPath
): boolean {
    return _.has(object, path);
}

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @category Object
 * @param object The object to query.
 * @param path The path to check.
 * @returns Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': _.create({ 'c': 3 }) }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b.c');
 * // => true
 *
 * _.hasIn(object, ['a', 'b', 'c']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn<T>(
    object: T,
    path:_.PropertyPath
): boolean {
    return _.hasIn(object, path)
};

/**
 * Creates an object composed of the picked `object` properties.
 *
 * @category Object
 * @param object The source object.
 * @param [props] The property names to pick, specified
 *  individually or in arrays.
 * @returns Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.pick(object, ['a', 'c']);
 * // => { 'a': 1, 'c': 3 }
 */
function pick<T extends object, U extends keyof T>(
    object: T,
    ...props: Array<_.Many<U>>
): Pick<T, U>;
function pick<T>(
    object: TypeOrNull<T>,
    ...props: _.PropertyPath[]
): _.PartialDeep<T>;
function pick(object, ...props) {
    return _.pick(object, ...props);
}

/**
* The opposite of `_.pick`; this method creates an object composed of the
* own and inherited enumerable properties of `object` that are not omitted.
*
* @category Object
* @param object The source object.
* @param [paths] The property names to omit, specified
*  individually or in arrays..
* @returns Returns the new object.
* @example
*
* var object = { 'a': 1, 'b': '2', 'c': 3 };
*
* _.omit(object, ['a', 'c']);
* // => { 'b': '2' }
*/
function omit<T extends _.AnyKindOfDictionary>(
    object: T | null | undefined,
    ...paths: Array<_.Many<_.PropertyName>>
): T;

/**
 * @see _.omit
 */
function omit<T extends object, K extends keyof T>(
    object: T | null | undefined,
    ...paths: Array<_.Many<K>>
): Omit<T, K>;

/**
 * @see _.omit
 */
function omit<T extends object>(
    object: T | null | undefined,
    ...paths: Array<_.Many<_.PropertyName>>
): _.PartialObject<T>;

function omit(object, ...paths) {
    return _.omit(object, ...paths);
}


/**
 * Creates an array of the own enumerable property names of object.
 *
 * Note: Non-object values are coerced to objects. See the ES spec for more details.
 *
 * @param object The object to query.
 * @return Returns the array of property names.
 */
function keys(object?: any): string[] {
    return _.keys(object);
}

/**
 * An alternative to _.reduce; this method transforms object to a new accumulator object which is the result of
 * running each of its own enumerable properties through iteratee, with each invocation potentially mutating
 * the accumulator object. The iteratee is invoked with four arguments: (accumulator,
 * value, key, object). Iteratee functions may exit iteration early by explicitly returning false.
 *
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @param accumulator The custom accumulator value.
 * @return Returns the accumulated value.
 */
function transform<T, TResult>(
    object: T[],
    iteratee: _.MemoVoidArrayIterator<T, TResult[]>,
    accumulator?: TResult[]
): TResult[] {
    return _.transform(object, iteratee, accumulator);
}

/**
 * Recursively merges own and inherited enumerable properties of source
 * objects into the destination object, skipping source properties that resolve
 * to `undefined`. Array and plain object properties are merged recursively.
 * Other objects and value types are overridden by assignment. Source objects
 * are applied from left to right. Subsequent sources overwrite property
 * assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @category Object
 * @param object The destination object.
 * @param [sources] The source objects.
 * @returns Returns `object`.
 * @example
 *
 * var users = {
 *   'data': [{ 'user': 'barney' }, { 'user': 'fred' }]
 * };
 *
 * var ages = {
 *   'data': [{ 'age': 36 }, { 'age': 40 }]
 * };
 *
 * _.merge(users, ages);
 * // => { 'data': [{ 'user': 'barney', 'age': 36 }, { 'user': 'fred', 'age': 40 }] }
 */
function merge<TObject, TSource>(
    object: TObject,
    source: TSource
): MergedTypes2<TObject, TSource>;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2>(
    object: TObject,
    source1: TSource1,
    source2: TSource2
): MergedTypes3<TObject, TSource1, TSource2>;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2, TSource3>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3
): MergedTypes4<TObject, TSource1, TSource2, TSource3>;

/**
 * @see _.merge
 */
function merge<TObject, TSource1, TSource2, TSource3, TSource4>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3,
    source4: TSource4
): MergedTypes5<TObject, TSource1, TSource2, TSource3, TSource4>;

/**
 * @see _.merge
 */
function merge(
    object: any,
    ...otherArgs: any[]
): any;
function merge(object, ...otherArgs) {
    return _.merge(object, ...otherArgs);
}

function fpMerge(source: IMap<any>, target: IMap<any>): IMap<any>;
function fpMerge(...args) {
    return fp.merge(...args);
}

/**
* @see _.extend
*/
function extend<TObject, TSource>(
    object: TObject,
    source: TSource
): MergedTypes2<TObject, TSource> {
    return _.extend(object, source);
}

/**
 * The opposite of _.mapValues; this method creates an object with the same values as object and keys generated
 * by running each own enumerable property of object through iteratee.
 *
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns the new mapped object.
 */
function mapKeys<T>(
object: _.List<T> | null | undefined,
iteratee?: _.ListIteratee<T>
): _.Dictionary<T>;

/**
 * @see _.mapKeys
 */
function mapKeys<T extends object>(
object: TypeOrNull<T>,
iteratee?: _.ObjectIteratee<T>
): _.Dictionary<T[keyof T]>;

function mapKeys (object, iteratee) {
    return _.mapKeys(object, iteratee)
}

/**
 * The opposite of `_.pickBy`; this method creates an object composed of the
 * own and inherited enumerable properties of `object` that `predicate`
 * doesn't return truthy for.
 *
 * @category Object
 * @param object The source object.
 * @param [predicate=_.identity] The function invoked per property.
 * @returns Returns the new object.
 * @example
 *
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 *
 * _.omitBy(object, _.isNumber);
 * // => { 'b': '2' }
 */
function omitBy<T>(object: DictionaryOrNull<T>, predicate?: _.ValueKeyIteratee<T>): _.Dictionary<T>;
/**
 * @see _.omitBy
 */
function omitBy<T>(object: NumericDictionaryOrNull<T>, predicate?: _.ValueKeyIteratee<T>): _.NumericDictionary<T>;
/**
 * @see _.omitBy
 */
function omitBy<T extends object>(object: TypeOrNull<T>, predicate: _.ValueKeyIteratee<T[keyof T]>): _.PartialObject<T>;

function omitBy(object, predicate?) {
    return _.omitBy(object, predicate)
}

/**
 * This method creates an object composed of the
 * own and inherited enumerable properties of `object` that `predicate`
 * return truthy for.
 *
 * @category Object
 * @param object The source object.
 * @param [predicate=_.identity] The function invoked per property.
 * @returns Returns the new object.
 * @example
 *
 * var object = { 'a': '1', 'b': 2, 'c': '3' };
 *
 * _.pickBy(object, _.isNumber);
 * // => { 'b': 2 }
 */
function pickBy<T>(object: DictionaryOrNull<T>, predicate?: _.ValueKeyIteratee<T>): _.Dictionary<T>;
/**
 * @see _.pickBy
 */
function pickBy<T>(object: NumericDictionaryOrNull<T>, predicate?: _.ValueKeyIteratee<T>): _.NumericDictionary<T>;
/**
 * @see _.pickBy
 */
function pickBy<T extends object>(object: TypeOrNull<T>, predicate: _.ValueKeyIteratee<T[keyof T]>): _.PartialObject<T>;

function pickBy(object, predicate?) {
    return _.pickBy(object, predicate)
}

/**
 * This method converts an object into an array of key-value pairs.
 * @param object The object to convert.
 * @return Returns the new array of key-value pairs.
 * @example
 * 
 * var object = { 'a': 1, 'b': 2 };
 * 
 * _.toPairs(object);
 * // => [['a', 1], ['b', 2]]
 */
function toPairs(object?: _.Dictionary<unknown> | _.NumericDictionary<unknown>): [string, unknown][];
function toPairs(object) {
    return _.toPairs(object);
}

/**
 * Assigns properties from source objects to the destination object
 * @param object The destination object.
 * @param [sources] The source objects.
 * @returns Returns `object`.
 * @example
 * var object = { 'a': 1, 'b': 2 };
 * 
 * _.assign(object, { 'b': 3, 'c': 4 });
 * // => { 'a': 1, 'b': 3, 'c': 4 }
 */
function assign<TObject, TSource>(
    object: TObject,
    source: TSource
): MergedTypes2<TObject, TSource>;
/**
 * @see _.assign
 */
function assign<TObject, TSource1, TSource2>(
    object: TObject,
    source1: TSource1,
    source2: TSource2
): MergedTypes3<TObject, TSource1, TSource2>;
/**
 * @see _.assign
 */
function assign<TObject, TSource1, TSource2, TSource3>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3
): MergedTypes4<TObject, TSource1, TSource2, TSource3>;
/**
 * @see _.assign
 */
function assign(
    object: any,
    ...otherArgs: any[]
): any;
function assign(object, ...otherArgs) {
    return _.assign(object, ...otherArgs);
}

/**
 * assigns default values to an object
 * @param object The destination object.
 * @param [sources] The source objects.
 * @returns Returns `object`.
 * @example
 * 
 * var object = { 'a': 1, 'b': 2 };
 * 
 * _.defaults(object, { 'b': 3, 'c': 4 });
 * // => { 'a': 1, 'b': 2, 'c': 4 }
 */

function defaults<TObject, TSource>(
    object: TObject,
    source: TSource
): MergedTypes2<TObject, TSource>;
/**
 * @see _.defaults
 */
function defaults<TObject, TSource1, TSource2>(
    object: TObject,
    source1: TSource1,
    source2: TSource2
): MergedTypes3<TObject, TSource1, TSource2>;
/**
 * @see _.defaults
 */
function defaults<TObject, TSource1, TSource2, TSource3>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3
): MergedTypes4<TObject, TSource1, TSource2, TSource3>;
/**
 * @see _.defaults
 */
function defaults(
    object: any,
    ...otherArgs: any[]
): any;
function defaults(object, ...otherArgs) {
    return _.defaults(object, ...otherArgs);
}

/**
 * Returns an array of the object\'s own enumerable string keyed property values
 * @param object The object to query.
 * @return Returns the array of property values.
 * @example
 * 
 * function Foo() {
 * this.a = 1;
 * this.b = 2;
 * }
 * 
 * Foo.prototype.c = 3;
 * 
 * _.values(new Foo);
 * // => [1, 2] (iteration order is not guaranteed)
 *
 */
function values<T>(object: _.Dictionary<T> | _.NumericDictionary<T> | _.List<T> | null | undefined): T[];
function values<T extends object>(object: T | null | undefined): Array<T[keyof T]>;
function values(object: any): any[];
function values(object) {
    return _.values(object);
}

/**
 * Iterates over the object\'s own enumerable string keyed properties
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns `object`.
 * @example
 * 
 * function Foo() {
 *  this.a = 1;
 * this.b = 2;
 * }
 * 
 * Foo.prototype.c = 3;
 * 
 * _.forOwn(new Foo, function(value, key) {
 * console.log(key);
 * });
 * // => logs 'a' and 'b' (iteration order is not guaranteed)
 */
function forOwn<T>(
    object: T,
    iteratee?: _.ObjectIteratee<T>
): T;
function forOwn(object, iteratee?) {
    return _.forOwn(object, iteratee);
}

/**
 * Applies function to each value in the object
 * @param object The object to iterate over
 * @param iteratee The function invoked per iteration
 * @return Returns the new mapped object
 * 
 * @example
 * 
 * var object = { 'a': 1, 'b': '2', 'c': 3 };
 * 
 * _.mapValues(object, value => value * 2);
 * // => { 'a': 2, 'b': '4', 'c': 6 }
 */
function mapValues<T>(
    object: T,
    iteratee: _.ObjectIteratee<T>
): _.Dictionary<any>;
function mapValues(object, iteratee) {
    return _.mapValues(object, iteratee);
}

/**
 * This method is like _.merge except that it accepts customizer which is invoked to produce the merged values of the destination and source properties. If customizer returns undefined, merging is handled by the method instead. The customizer is invoked with six arguments: (objValue, srcValue, key, object, source, stack).
 * @param object The destination object.
 * @param sources The source objects.
 * @param customizer The function to customize assigned values.
 * @return Returns `object`.
 * @example
 * 
 * function customizer(objValue, srcValue) {
 * return _.isUndefined(objValue) ? srcValue : objValue;
 * }
 * 
 * var object = { 'a': [1], 'b': [2] };
 * var other = { 'a': [3], 'b': [4] };
 * 
 * _.mergeWith(object, other, customizer);
 * // => { 'a': [1, 3], 'b': [2, 4] }
 * 
 * var object = { 'a': [1], 'b': [2] };
 * var other = { 'a': [3], 'b': [4] };
 */
function mergeWith<TObject, TSource>(
    object: TObject,
    source: TSource,
    customizer: _.MergeWithCustomizer
): MergedTypes2<TObject, TSource>;

/**
 * @see _.mergeWith
 */
function mergeWith<TObject, TSource1, TSource2>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    customizer: _.MergeWithCustomizer
): MergedTypes3<TObject, TSource1, TSource2>;

/**
 * @see _.mergeWith
 */
function mergeWith<TObject, TSource1, TSource2, TSource3>(
    object: TObject,
    source1: TSource1,
    source2: TSource2,
    source3: TSource3,
    customizer: _.MergeWithCustomizer
): MergedTypes4<TObject, TSource1, TSource2, TSource3>;

/**
 * @see _.mergeWith
 */
function mergeWith(
    object: any,
    ...otherArgs: any[]
): any;

function mergeWith(object, ...otherArgs) {
    return _.mergeWith(object, ...otherArgs);
}

/**
 * Removes the property at path of object.
 * @param object The object to modify.
 * @param path The path of the property to unset.
 * @return Returns `true` if the property is deleted, else `false`.
 * @example
 * 
 * var object = { 'a': [{ 'b': { 'c': 7 } }] };
 * _.unset(object, 'a[0].b.c');
 * // => true
 * 
 * console.log(object);
 * // => { 'a': [{ 'b': {} }] };
 */
function unset(object: any, path: _.PropertyPath): boolean {
    return _.unset(object, path);
}

/**
 * Gets the value at path of object. If the resolved value is undefined, the defaultValue is returned in its place.
 * @param object The object to query.
 * @param path The path of the property to get.
 * @param defaultValue The value returned for undefined resolved values.
 * @return Returns the resolved value.
 * @example
 * 
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 * 
 * _.result(object, 'a[0].b.c');
 * // => 3
 * 
 * _.result(object, 'a[0].b.c', 'default');
 * // => 3
 * 
 * _.result(object, 'a[0].b.d', 'default');
 * // => 'default'
 */
function result(object: any, path: _.PropertyPath, defaultValue?: any): any {
    return _.result(object, path, defaultValue);
}

/**
 * Iterates over own enumerable string keyed properties of an object and invokes iteratee for each property.
 * The iteratee is invoked with three arguments: (value, key, object).
 * Iteratee functions may exit iteration early by explicitly returning false.
 * @param object The object to iterate over.
 * @param iteratee The function invoked per iteration.
 * @return Returns `object`.
 * @example
 * 
 * function Foo() {
 * this.a = 1;
 * this.b = 2;
 * }
 * 
 * Foo.prototype.c = 3;
 * 
 * _.forIn(new Foo, function(value, key) {
 * console.log(key);
 * });
 * // => logs 'a', 'b', and 'c' (iteration order is not guaranteed)
 */
function forIn(object, iteratee) {
    return _.forIn(object, iteratee);
}

/**
 * Set the value at path of object. If a portion of path doesn't exist, it's created. Arrays are created for missing index properties while objects are created for all other missing properties.
 * @param object The object to modify.
 * @param path The path of the property to set.
 * @param value The value to set.
 * @return Returns object.
 * @example
 * 
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 * 
 * _.set(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 */
function set(object: any, path: _.PropertyPath, value: any): any {
    return _.set(object, path, value);
}

export const ObjectUtil = {
    get, has, hasIn, pick, omit, keys, transform, merge, fpMerge, extend, mapKeys, omitBy, pickBy, toPairs, assign, defaults, values, forOwn, mapValues, mergeWith, unset, result, forIn, set
}

export default ObjectUtil;